import type { HookCode, WorkflowJsonObject } from '@atlas/types'
import { HookStage, TransitionDirection } from '@atlas/types'
import type { TransitionHookDTO } from '@/domain/workflow-admin.types'
import type { BuilderDirection, BuilderEdge } from '@/domain/workflow-builder-layout'
import { sequenceAgreementOf } from '@/domain/workflow-builder-layout'
import { defaultHookParams } from '@/domain/workflow-hook-catalog'
import {
	useCreateTransitionHook,
	useDeleteTransitionHook,
	useTransitionHooks,
	useUpdateTransitionHook,
} from '@/hooks/useTransitionHookAdmin'
import type {
	useCreateWorkflowTransition,
	useDeleteWorkflowTransition,
	useUpdateWorkflowTransition,
} from '@/hooks/useWorkflowAdmin'
import { notifyError } from './notifyError'
import { WorkflowTransitionInspector } from './WorkflowTransitionInspector'

function buildTransitionHandlers({
	active,
	activeWorkflowId,
	hooks,
	updateTransition,
	createTransition,
	deleteTransition,
	createHook,
	updateHook,
	deleteHook,
	edge,
	onDeleted,
}: {
	active: BuilderDirection | null
	activeWorkflowId: string | null
	hooks: readonly TransitionHookDTO[]
	updateTransition: ReturnType<typeof useUpdateWorkflowTransition>
	createTransition: ReturnType<typeof useCreateWorkflowTransition>
	deleteTransition: ReturnType<typeof useDeleteWorkflowTransition>
	createHook: ReturnType<typeof useCreateTransitionHook>
	updateHook: ReturnType<typeof useUpdateTransitionHook>
	deleteHook: ReturnType<typeof useDeleteTransitionHook>
	edge: BuilderEdge
	onDeleted: () => void
}) {
	const addBackward = () => {
		if (!activeWorkflowId) return
		createTransition.mutate(
			{
				workflowId: activeWorkflowId,
				fromStatusId: edge.toId,
				toStatusId: edge.fromId,
				ctaLabel: 'Send back',
				direction: TransitionDirection.BACKWARD,
			},
			{ onError: notifyError('Could not add the backward transition') },
		)
	}

	const deleteDirection = () => {
		if (!active) return
		deleteTransition.mutate(
			{ transitionId: active.transitionId, workflowId: activeWorkflowId as string },
			{ onSuccess: onDeleted, onError: notifyError('Could not delete this transition') },
		)
	}

	// `position` defaults to 0 server-side if omitted — every hook past the
	// first one on a given stage would collide with an existing row's position
	// under the stage's own uniqueness constraint. One more than the current
	// max (not `.length`) survives gaps left by a removed hook, e.g. positions
	// [0, 2] after 1 was deleted.
	const attachHook = (stage: HookStage, hookCode: HookCode) => {
		if (!active) return
		const stageHooks = hooks.filter((h) => h.stage === stage)
		const position = stageHooks.length ? Math.max(...stageHooks.map((h) => h.position)) + 1 : 0
		createHook.mutate(
			{ transitionId: active.transitionId, stage, hookCode, params: defaultHookParams(hookCode), position },
			{ onError: notifyError('Could not attach that hook') },
		)
	}

	const onChangeCta = (cta: string) =>
		active &&
		updateTransition.mutate(
			{ transitionId: active.transitionId, body: { ctaLabel: cta }, workflowId: activeWorkflowId as string },
			{ onError: notifyError('Could not save the CTA label') },
		)

	const onRemoveHook = (hookId: string) =>
		active &&
		deleteHook.mutate(
			{ hookId, transitionId: active.transitionId },
			{ onError: notifyError('Could not remove that hook') },
		)

	const onUpdateHookParams = (hookId: string, params: WorkflowJsonObject) =>
		active &&
		updateHook.mutate(
			{ hookId, body: { params }, transitionId: active.transitionId },
			{ onError: notifyError('Could not save the hook configuration') },
		)

	return { addBackward, deleteDirection, attachHook, onChangeCta, onRemoveHook, onUpdateHookParams }
}

/**
 * Bridges a selected connector to `WorkflowTransitionInspector` — resolves
 * which real `transitionId` the active `dir` denotes (if any), fetches its
 * hooks, and translates the panel's edit callbacks into the real mutations.
 */
export function TransitionInspectorPanel({
	edge,
	dir,
	fromLabel,
	toLabel,
	fromPosition,
	toPosition,
	activeWorkflowId,
	templates,
	updateTransition,
	createTransition,
	deleteTransition,
	onSwitchDir,
	onDeleted,
}: {
	edge: BuilderEdge
	dir: 'forward' | 'backward'
	fromLabel: string
	toLabel: string
	/** The connector's forward-orientation step positions, for checking a stored direction against the order. */
	fromPosition: number
	toPosition: number
	activeWorkflowId: string | null
	templates: readonly { code: string; label: string }[]
	updateTransition: ReturnType<typeof useUpdateWorkflowTransition>
	createTransition: ReturnType<typeof useCreateWorkflowTransition>
	deleteTransition: ReturnType<typeof useDeleteWorkflowTransition>
	onSwitchDir: (dir: 'forward' | 'backward') => void
	onDeleted: () => void
}) {
	const active = dir === 'forward' ? edge.forward : edge.backward
	const { data: hooks = [] } = useTransitionHooks(active?.transitionId ?? null)
	const createHook = useCreateTransitionHook()
	const updateHook = useUpdateTransitionHook()
	const deleteHook = useDeleteTransitionHook()

	const { addBackward, deleteDirection, attachHook, onChangeCta, onRemoveHook, onUpdateHookParams } =
		buildTransitionHandlers({
			active,
			activeWorkflowId,
			hooks,
			updateTransition,
			createTransition,
			deleteTransition,
			createHook,
			updateHook,
			deleteHook,
			edge,
			onDeleted,
		})

	// Both rows of a connector are the same traversal read two ways, so an
	// inverted connector has both of them wrong: the repair flips whichever
	// exist. Flipping one alone would leave two rows claiming one direction.
	const agreement = sequenceAgreementOf(fromPosition, toPosition)
	const fixDirection = () => {
		if (agreement !== 'disagrees') return
		if (edge.forward) {
			updateTransition.mutate(
				{
					transitionId: edge.forward.transitionId,
					body: { direction: TransitionDirection.BACKWARD },
					workflowId: activeWorkflowId as string,
				},
				{ onError: notifyError('Could not change the transition direction') },
			)
		}
		if (edge.backward) {
			updateTransition.mutate(
				{
					transitionId: edge.backward.transitionId,
					body: { direction: TransitionDirection.FORWARD },
					workflowId: activeWorkflowId as string,
				},
				{ onError: notifyError('Could not change the transition direction') },
			)
		}
	}

	return (
		<WorkflowTransitionInspector
			fromLabel={dir === 'forward' ? fromLabel : toLabel}
			toLabel={dir === 'forward' ? toLabel : fromLabel}
			dir={dir}
			hasForward={!!edge.forward}
			hasBackward={!!edge.backward}
			onSwitchDir={onSwitchDir}
			onAddBackward={addBackward}
			ctaLabel={active?.ctaLabel ?? ''}
			onChangeCta={onChangeCta}
			preHooks={hooks.filter((h) => h.stage === HookStage.PRE)}
			postHooks={hooks.filter((h) => h.stage === HookStage.POST)}
			templates={templates}
			isAttachingHook={createHook.isPending}
			onAttachHook={attachHook}
			onRemoveHook={onRemoveHook}
			onUpdateHookParams={onUpdateHookParams}
			onDeleteDirection={deleteDirection}
			sequenceAgreement={agreement}
			onFixDirection={fixDirection}
			isFixingDirection={updateTransition.isPending}
		/>
	)
}
